import { IValidationReport, IValidationResult } from '../types';
import { formatData, formatChartTaleDta } from '@/pages/aigc/pages/Agent/ChatPage/components/MainContent/_utils';

/**
 * biData 数据验证器
 * 实现5层检测机制：
 * L1: JSON 语法检测
 * L2: 数据类型检测
 * L3: 数据结构检测
 * L4: 数据内容检测
 * L5: 数据转换检测
 */
export class BiDataValidator {
  private errors: IValidationResult[] = [];
  private warnings: IValidationResult[] = [];
  private successes: IValidationResult[] = [];

  /**
   * 执行完整的数据验证
   * @param input - JSON 字符串输入
   * @returns 验证报告
   */
  public validate(input: string): IValidationReport {
    this.reset();

    // L1: JSON 语法检测
    const parseResult = this.validateJson(input);
    if (!parseResult.success) {
      return this.generateReport(false);
    }

    let {data} = parseResult;

    // L2: 数据类型检测
    if (!this.validateDataType(data)) {
      return this.generateReport(false);
    }

    // 标准化为数组格式
    data = Array.isArray(data) ? data : [data];

    // L3: 数据结构检测
    if (!this.validateStructure(data)) {
      return this.generateReport(false);
    }

    // L4: 数据内容检测
    this.validateContent(data);

    // 如果有错误，不继续 L5 检测
    if (this.errors.length > 0) {
      return this.generateReport(false);
    }

    // L5: 数据转换检测
    const canRender = this.validateTransformation(data);

    return this.generateReport(canRender, data);
  }

  /**
   * L1: JSON 语法检测
   * @param input - 输入字符串
   * @returns 解析结果
   */
  private validateJson(input: string): { success: boolean; data?: any; } {
    // 检查空输入
    if (!input || !input.trim()) {
      this.addError('输入内容为空', 'input');
      return { success: false };
    }

    try {
      const data = JSON.parse(input);
      this.addSuccess('✓ JSON 格式验证通过');
      return { success: true, data };
    } catch (error: any) {
      // 提取错误位置信息
      const match = error.message.match(/position (\d+)/);
      const position = match ? parseInt(match[1]) : undefined;

      const errorMsg = `JSON 解析失败: ${error.message}`;
      const lineColumn = position ? this.getLineColumn(input, position) : undefined;

      this.addError(errorMsg, 'json', error.message, lineColumn);
      return { success: false };
    }
  }

  /**
   * L2: 数据类型检测
   * @param data - 解析后的数据
   * @returns 是否通过检测
   */
  private validateDataType(data: any): boolean {
    if (typeof data !== 'object' || data === null) {
      this.addError('数据格式错误：应为对象或数组', 'root');
      return false;
    }

    this.addSuccess('✓ 数据类型验证通过');
    return true;
  }

  /**
   * L3: 数据结构检测
   * @param dataArray - 数据数组
   * @returns 是否通过检测
   */
  private validateStructure(dataArray: any[]): boolean {
    let hasError = false;

    dataArray.forEach((item, index) => {
      const prefix = dataArray.length > 1 ? `[${index}]` : '';

      // 检查 data 字段
      if (!('data' in item)) {
        this.addError(`${prefix}缺少必要字段: data`, `${prefix}.data`);
        hasError = true;
        return;
      }

      if (typeof item.data !== 'object' || item.data === null) {
        this.addError(`${prefix}data 应为对象类型`, `${prefix}.data`);
        hasError = true;
        return;
      }

      // 检查必要字段
      const requiredFields = ['metrics_info', 'dimension_info', 'data', 'xAxis'];

      requiredFields.forEach((field) => {
        if (!(field in item.data)) {
          this.addError(`${prefix}缺少必要字段: data.${field}`, `${prefix}.data.${field}`);
          hasError = true;
        } else if (!Array.isArray(item.data[field])) {
          const actualType = item.data[field] === null ? 'null' : typeof item.data[field];
          this.addError(
            `${prefix}data.${field} 应为数组类型，实际为 ${actualType}`,
            `${prefix}.data.${field}`
          );
          hasError = true;
        }
      });
    });

    if (!hasError) {
      this.addSuccess('✓ 数据结构验证通过');
    }

    return !hasError;
  }

  /**
   * L4: 数据内容检测（警告级别）
   * @param dataArray - 数据数组
   */
  private validateContent(dataArray: any[]): void {
    dataArray.forEach((item, index) => {
      const prefix = dataArray.length > 1 ? `[${index}]` : '';
      const { metrics_info, dimension_info, data } = item.data;

      // 检查空数组
      if (metrics_info && metrics_info.length === 0) {
        this.addWarning(
          `${prefix}metrics_info 为空，图表可能无法正常显示`,
          `${prefix}.data.metrics_info`
        );
      }

      if (data && data.length === 0) {
        this.addWarning(`${prefix}data 为空，图表将无数据展示`, `${prefix}.data.data`);
      }

      // 检查 metrics_info 元素
      if (metrics_info && Array.isArray(metrics_info)) {
        metrics_info.forEach((metric: any, idx: number) => {
          if (!metric.data_key) {
            this.addError(
              `${prefix}metrics_info[${idx}] 缺少必填字段 data_key`,
              `${prefix}.data.metrics_info[${idx}].data_key`
            );
          } else if (typeof metric.data_key !== 'string' || !metric.data_key.trim()) {
            this.addError(
              `${prefix}metrics_info[${idx}].data_key 应为非空字符串`,
              `${prefix}.data.metrics_info[${idx}].data_key`
            );
          }
        });
      }

      // 检查 dimension_info 元素
      if (dimension_info && Array.isArray(dimension_info)) {
        dimension_info.forEach((dimension: any, idx: number) => {
          if (!dimension.data_key) {
            this.addError(
              `${prefix}dimension_info[${idx}] 缺少必填字段 data_key`,
              `${prefix}.data.dimension_info[${idx}].data_key`
            );
          } else if (typeof dimension.data_key !== 'string' || !dimension.data_key.trim()) {
            this.addError(
              `${prefix}dimension_info[${idx}].data_key 应为非空字符串`,
              `${prefix}.data.dimension_info[${idx}].data_key`
            );
          }
        });
      }
    });

    if (this.errors.length === 0) {
      this.addSuccess('✓ 数据内容验证通过');
    }
  }

  /**
   * L5: 数据转换检测（关键）
   * 使用实际的 formatData 和 formatChartTaleDta 函数进行检测
   * @param dataArray - 数据数组
   * @returns 是否可以渲染
   */
  private validateTransformation(dataArray: any[]): boolean {
    try {
      // 动态导入实际的转换函数
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      // const { formatData, formatChartTaleDta } = require(
      //   '@/pages/aigc/pages/Agent/ChatPage/components/MainContent/_utils'
      // );

      // 步骤1: formatData 转换检测
      let transformedData;
      try {
        transformedData = formatData(dataArray);
        this.addSuccess('✓ formatData 转换成功');
      } catch (error: any) {
        console.error('[BiDataValidator] formatData 转换失败:', error);
        this.addError(
          `formatData 转换失败: ${error.message}`,
          'transformation.formatData',
          error.stack
        );
        return false;
      }

      // 步骤2: formatChartTaleDta 转换检测
      try {
        const finalData = formatChartTaleDta(transformedData);

        // 验证转换后的数据完整性
        if (!finalData || typeof finalData !== 'object') {
          this.addError(
            'formatChartTaleDta 返回数据格式异常',
            'transformation.formatChartTaleDta'
          );
          return false;
        }

        this.addSuccess('✓ formatChartTaleDta 转换成功');
        this.addSuccess('✓ 数据转换检测通过，可以渲染');
        return true;
      } catch (error: any) {
        console.error('[BiDataValidator] formatChartTaleDta 转换失败:', error);
        this.addError(
          `formatChartTaleDta 转换失败: ${error.message}`,
          'transformation.formatChartTaleDta',
          error.stack
        );
        return false;
      }
    } catch (error: any) {
      console.error('[BiDataValidator] 转换检测初始化失败:', error);
      this.addError(`转换检测初始化失败: ${error.message}`, 'transformation', error.stack);
      return false;
    }
  }

  /**
   * 添加错误信息
   */
  private addError(
    message: string,
    field?: string,
    details?: string,
    position?: { line: number; column: number; }
  ): void {
    this.errors.push({ level: 'error', message, field, details, position });
  }

  /**
   * 添加警告信息
   */
  private addWarning(message: string, field?: string, details?: string): void {
    this.warnings.push({ level: 'warning', message, field, details });
  }

  /**
   * 添加成功项
   */
  private addSuccess(message: string, field?: string): void {
    this.successes.push({ level: 'success', message, field });
  }

  /**
   * 重置验证状态
   */
  private reset(): void {
    this.errors = [];
    this.warnings = [];
    this.successes = [];
  }

  /**
   * 生成验证报告
   */
  private generateReport(canRender: boolean, inputData?: any): IValidationReport {
    return {
      passed: this.errors.length === 0,
      canRender,
      errors: this.errors,
      warnings: this.warnings,
      successes: this.successes,
      timestamp: Date.now(),
      inputData,
    };
  }

  /**
   * 获取错误的行列位置
   */
  private getLineColumn(text: string, position: number): { line: number; column: number; } {
    const lines = text.substring(0, position).split('\n');
    return {
      line: lines.length,
      column: lines[lines.length - 1].length + 1,
    };
  }
}

